import { UserAvatar } from "@/components/ui/stacked-avatars";
import { cn } from "@/lib/utils";
import { Scribbles } from "@somethings-ui/core/assets";
import type {
  HotTakeAnswer,
  HotTakesConfigStatement,
} from "@somethings-ui/core/hot-takes";
import { Check, X } from "lucide-react";

interface HotTakesSummaryCardProps {
  mentorName?: string | null;
  teenName: string;
  matchesCount: number;
  mentorAvatarUrl: string | null;
  teenAvatarUrl: string | null;
  statements: HotTakesConfigStatement[];
  teenAnswerById: Map<string, HotTakeAnswer>;
  // When true wraps card in a clickable container that forwards to onClick.
  onClick?: () => void;
}

// Shared purple "Summary" card used in both the chat timeline and the Hub
// side view. Fixed header (title + subtitle + avatars) stays pinned while the
// statement list scrolls independently.
export function HotTakesSummaryCard({
  mentorName,
  teenName,
  matchesCount,
  mentorAvatarUrl,
  teenAvatarUrl,
  statements,
  teenAnswerById,
  onClick,
}: HotTakesSummaryCardProps) {
  const interactive = !!onClick;

  const content = (
    <div
      className={cn(
        "relative max-h-[538px] w-[361px] overflow-hidden rounded-[24px] shadow-[0_0_16px_rgba(0,0,0,0.08)]",
        interactive && "cursor-pointer text-left",
      )}
      style={{
        backgroundImage:
          "linear-gradient(180deg, #322964 0%, #1D1F46 100%)",
      }}
    >
      <img
        src={Scribbles.purpleCloudTilted}
        alt=""
        aria-hidden
        className="pointer-events-none absolute left-0 right-0 top-0 w-full object-contain"
      />

      <div className="relative flex max-h-[538px] flex-col">
        {/* Fixed header */}
        <div className="flex flex-none flex-col items-center gap-[32px] px-4 pt-16">
          <div className="flex flex-col items-center gap-1 px-2 text-center">
            <h3 className="text-[32px] font-bold leading-[1.05] tracking-tight text-white">
              Summary
            </h3>
            <p className="text-base text-white">
              You &amp; {teenName} agree on {matchesCount} take
              {matchesCount === 1 ? "" : "s"}
            </p>
          </div>

          {/* Avatars row mirrors statement-row layout */}
          <div className="flex w-full items-center gap-8">
            <div className="w-[173px] shrink-0" aria-hidden />
            <div className="flex flex-1 items-center justify-between">
              <div className="-ml-[3px]">
                <AvatarCircle name={mentorName || "Mentor"} src={mentorAvatarUrl} />
              </div>
              <AvatarCircle name={teenName || "Teen"} src={teenAvatarUrl} />
            </div>
          </div>
        </div>

        {/* Scrollable statements */}
        <div className="no-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-16 pt-[23px]">
          <div className="flex w-full flex-col gap-6">
            {statements.map((s) => {
              const teenAnswer = teenAnswerById.get(s.id) ?? null;
              const matches =
                teenAnswer !== null && teenAnswer === s.mentorAnswer;
              return (
                <div key={s.id} className="flex items-center gap-8">
                  <p className="w-[173px] shrink-0 break-words text-sm font-medium leading-tight text-white">
                    {s.text}
                  </p>
                  <div className="flex flex-1 items-center justify-between">
                    <AnswerCircle answer={s.mentorAnswer} matches={matches} />
                    <AnswerCircle answer={teenAnswer} matches={matches} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  if (!interactive) return content;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      {content}
    </div>
  );
}

function AvatarCircle({ name, src }: { name: string; src: string | null }) {
  return (
    <UserAvatar
      imageUrl={src ?? undefined}
      name={name}
      size={28}
      showBorder
    />
  );
}

function AnswerCircle({
  answer,
  matches,
}: {
  answer: HotTakeAnswer | null;
  matches: boolean;
}) {
  if (!answer) {
    return <span className="inline-block h-6 w-6 shrink-0" aria-hidden />;
  }

  const isAgree = answer === "agree";

  return (
    <span
      className={cn(
        "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full p-1.5",
        matches
          ? "bg-white text-[#1d1f46]"
          : "border border-white text-white",
      )}
      aria-label={isAgree ? "Agree" : "Disagree"}
    >
      {isAgree ? (
        <Check className="h-3 w-3" strokeWidth={3} />
      ) : (
        <X className="h-3 w-3" strokeWidth={3} />
      )}
    </span>
  );
}
